import { FormEvent } from "react"
import { Link, useForm } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { MainContainer } from "@/components/ui/container"
import { PageHeader } from "@/components/ui/headers"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { t } from "@/lib/i18n"
type RoleOption = {
    value: string
    label: string
}
type FormUser = {
    id?: number
    userId?: number
    name?: string
    emailAddress?: string
    role?: string
}
type Props = {
    mode: "create" | "edit"
    title: string
    description: string
    roleOptions: RoleOption[]
    user?: FormUser
}
function errorMessage(value: string | string[] | undefined): string | null {
    if (!value)
        return null
    return Array.isArray(value) ? value[0] : value
}
export function StaffManageUserForm({ mode, title, description, roleOptions, user }: Props) {
    const { data, setData, post, put, errors, processing } = useForm({
        name: user?.name || "",
        email_address: user?.emailAddress || "",
        role: user?.role || "waiter",
        password: "",
        password_confirmation: "",
    })
    const baseError = errorMessage((errors as Record<string, string | string[] | undefined>).base)
    const shouldShowEmailField = data.role === "manager"
    const handleRoleChange = (nextRole: string) => {
        setData((current) => ({
            ...current,
            role: nextRole,
            email_address: nextRole === "manager" ? current.email_address : "",
        }))
    }
    const handleSubmit = (event: FormEvent) => {
        event.preventDefault()
        if (mode === "create") {
            post("/manage/users")
            return
        }
        put(`/manage/users/${user?.id}`)
    }
    return (<MainContainer size="sm">
      <PageHeader title={title} description={description}/>

      <Card data-testid="user-form-card">
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {baseError && (<p className="rounded-md border border-destructive/20 bg-destructive/5 p-3 text-sm text-destructive">
                {baseError}
              </p>)}

            <div className="space-y-2">
              <Label htmlFor="name">{t("ui.react.components.features.staff.manage_users.user_form.name")}</Label>
              <Input id="name" value={data.name} onChange={(event) => setData("name", event.target.value)} placeholder={t("ui.react.components.features.staff.manage_users.user_form.mary_smith")}/>
              {errors.name && (<p className="text-sm font-medium text-destructive">{errors.name}</p>)}
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">{t("ui.react.components.features.staff.manage_users.user_form.role")}</Label>
              <Select value={data.role} onValueChange={handleRoleChange}>
                <SelectTrigger id="role" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roleOptions.map((roleOption) => (<SelectItem key={roleOption.value} value={roleOption.value}>
                      {roleOption.label}
                    </SelectItem>))}
                </SelectContent>
              </Select>
              {errors.role && (<p className="text-sm font-medium text-destructive">{errors.role}</p>)}
            </div>

            {shouldShowEmailField && (<div className="space-y-2">
                <Label htmlFor="email_address">{t("ui.react.components.features.staff.manage_users.user_form.email")}</Label>
                <Input id="email_address" type="email" value={data.email_address} onChange={(event) => setData("email_address", event.target.value)} placeholder={t("ui.react.components.features.staff.manage_users.user_form.staff_example_com")}/>
                {errors.email_address && (<p className="text-sm font-medium text-destructive">{errors.email_address}</p>)}
              </div>)}

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="password">
                  {mode === "create" ? t("ui.staff.users.password_label_create") : t("ui.staff.users.password_label_edit")}
                </Label>
                <Input id="password" type="password" value={data.password} onChange={(event) => setData("password", event.target.value)} placeholder="••••••••"/>
                {errors.password && (<p className="text-sm font-medium text-destructive">{errors.password}</p>)}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password_confirmation">{t("ui.react.components.features.staff.manage_users.user_form.confirmation_password")}</Label>
                <Input id="password_confirmation" type="password" value={data.password_confirmation} onChange={(event) => setData("password_confirmation", event.target.value)} placeholder="••••••••"/>
                {errors.password_confirmation && (<p className="text-sm font-medium text-destructive">{errors.password_confirmation}</p>)}
              </div>
            </div>

            <p className="text-sm text-muted-foreground">
              {shouldShowEmailField
            ? t("ui.staff.users.manager_email_help")
            : t("ui.staff.users.non_manager_email_help")}
            </p>

            <p className="text-sm text-muted-foreground">
              {mode === "create"
            ? t("ui.staff.users.create_password_help")
            : t("ui.staff.users.edit_password_help")}
            </p>

            <div className="flex items-center gap-2">
              <Button type="submit" disabled={processing} data-testid={mode === "create" ? "create-user-button" : "update-user-button"}>
                {mode === "create" ? t("ui.staff.users.create_member") : t("ui.common.actions.save_changes")}
              </Button>
              <Button asChild variant="outline" type="button">
                <Link href="/manage/users">{t("ui.react.components.features.staff.manage_users.user_form.cancel")}</Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </MainContainer>)
}
